import React, { useState, useMemo } from "react";

import { IPFS_GATEWAY } from "../lib/media";

const FALLBACK_HOSTS = [IPFS_GATEWAY];

// Matches both well-formed (https://gw.example/ipfs/CID...) and schemeless
// (gw.example/ipfs/CID...) IPFS URLs. Group 1 = CID, Group 2 = optional path.
const IPFS_PATH_RE = /^(?:https?:\/\/)?[^/\s]+\/ipfs\/(?:ipfs\/)*([a-zA-Z0-9]+)(\/.*)?$/i;
// Also match subdomain form: https://<cid>.ipfs.<host>/<rest>
const IPFS_SUBDOMAIN_RE = /^(?:https?:\/\/)?([a-zA-Z0-9]+)\.ipfs\.[^/\s]+(\/.*)?$/i;

// Given any IPFS URL or raw CID, return an ordered list of candidate URLs.
function buildCandidates(input) {
    if (!input) return [];
    // Try to extract a CID either from a (possibly schemeless) URL or
    // assume the input itself is a raw CID.
    let cid = null;
    let rest = "";
    const mPath = input.match(IPFS_PATH_RE);
    const mSub = !mPath ? input.match(IPFS_SUBDOMAIN_RE) : null;
    if (mPath) {
        cid = mPath[1];
        rest = mPath[2] || "";
    } else if (mSub) {
        cid = mSub[1];
        rest = mSub[2] || "";
    } else if (/^(Qm[1-9A-HJ-NP-Za-km-z]{44}|baf[a-z2-7]+)$/.test(input)) {
        cid = input;
    }
    if (!cid) {
        // Not an IPFS resource we can rewrite — return original (with scheme if missing)
        return [input.startsWith("http") ? input : `https://${input}`];
    }
    const list = [];
    list.push(`${FALLBACK_HOSTS[0]}/ipfs/${cid}${rest}`);
    for (const h of FALLBACK_HOSTS.slice(1)) {
        list.push(`${h}/ipfs/${cid}${rest}`);
    }
    return list;
}

/**
 * <IpfsImage src=... /> — resilient image that cascades through public
 * IPFS gateways when the current one fails. Drop-in replacement for <img>.
 */
const IpfsImage = ({ src, fallback = null, onAllFailed, ...props }) => {
    const candidates = useMemo(() => buildCandidates(src), [src]);
    // Reset cascade when src changes (key on candidates length to bust stale state)
    const [state, setState] = useState({ idx: 0, allFailed: false, key: candidates.join("|") });
    const currentKey = candidates.join("|");
    if (state.key !== currentKey && (state.idx !== 0 || state.allFailed)) {
        setState({ idx: 0, allFailed: false, key: currentKey });
    }
    const { idx, allFailed } = state;

    const handleError = () => {
        setState((prev) => {
            if (prev.idx + 1 < candidates.length) {
                return { ...prev, idx: prev.idx + 1 };
            }
            if (!prev.allFailed && typeof onAllFailed === "function") onAllFailed();
            return { ...prev, allFailed: true };
        });
    };

    if (allFailed) return fallback;
    if (!candidates.length) return fallback;

    return (
        <img
            alt={props.alt || ""}
            src={candidates[idx]}
            onError={handleError}
            referrerPolicy="no-referrer"
            {...props}
        />
    );
};

export default IpfsImage;
